import AccountSelect from "../components/AccountSelect";
import CurrencySelect from "../components/CurrencySelect";
import FXPreview from "../components/FXPreview";
import FXDetails from "../components/FXDetails";
import { formatMoney } from "../lib/currency";
import { useState, useEffect } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { api, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { CurrencyInput } from "../components/ui/currency-input";
import { toast } from "@/lib/toast";
import { CreditCard, Plus, Trash2, Loader2, DollarSign, History, TrendingDown, Edit, Calendar } from "lucide-react";

const EMOJI_OPTIONS_CARDS = ["💳", "💎", "🚀", "🏦", "🛍️", "✈️", "🍔", "🚕", "💼", "💸", "🎁", "🎨", "🔧", "💻"];
const THEMES = [
    { name: "primary", bgLight: "bg-primary-container/40", bgFill: "bg-primary", txColor: "text-primary", btnClass: "bg-primary text-on-primary", icon: "💳" },
    { name: "secondary", bgLight: "bg-secondary-container/40", bgFill: "bg-secondary", txColor: "text-secondary", btnClass: "bg-secondary text-on-secondary", icon: "💎" },
    { name: "tertiary", bgLight: "bg-tertiary-container/40", bgFill: "bg-tertiary", txColor: "text-tertiary", btnClass: "bg-tertiary text-white", icon: "🚀" }
];

function getCatIconUrl(name) {
  const norm = (name || "").toLowerCase().trim();
  if (norm.includes("aliment") || norm.includes("comida")) return "/Iconos/Categorias/Alimnetacion.png";
  if (norm.includes("transporte") || norm.includes("carro") || norm.includes("moto") || norm.includes("uber")) return "/Iconos/Categorias/transporte.png";
  if (norm.includes("entretenimiento") || norm.includes("diversion") || norm.includes("cine")) return "/Iconos/Categorias/Entretenimiento.png";
  if (norm.includes("salud") || norm.includes("medica") || norm.includes("farmacia")) return "/Iconos/Categorias/salud.png";
  if (norm.includes("educac") || norm.includes("universidad") || norm.includes("colegio") || norm.includes("escuela") || norm.includes("curso")) return "/Iconos/Categorias/Educacion.png";
  if (norm.includes("hogar") || norm.includes("casa") || norm.includes("muebles")) return "/Iconos/Categorias/Hogar.png";
  if (norm.includes("servicios") || norm.includes("luz") || norm.includes("agua") || norm.includes("gas")) return "/Iconos/Categorias/Servicios.png";
  if (norm.includes("suscri") || norm.includes("netflix") || norm.includes("spotify")) return "/Iconos/Categorias/Suscripciones.png";
  if (norm.includes("viaje") || norm.includes("vuelo") || norm.includes("hotel")) return "/Iconos/Categorias/Viajes.png";
  if (norm.includes("deuda") || norm.includes("prestam") || norm.includes("banco")) return "/Iconos/Categorias/Deudas.png";
  if (norm.includes("ahorro") || norm.includes("meta") || norm.includes("piggy")) return "/Iconos/Categorias/Ahorros.png";
  if (norm.includes("deporte") || norm.includes("gym") || norm.includes("ejercicio") || norm.includes("futbol") || norm.includes("entreno")) return "/Iconos/Categorias/deporte.png";
  return "/Iconos/Categorias/otros.png";
}

function getCatIcon(icon, name="", size=24) {
  if (icon && typeof icon === "string" && (icon.startsWith("/") || icon.includes(".png"))) {
    return <img src={icon} alt={name || "category"} style={{ width: size, height: size, objectFit: "contain" }} />;
  }
  const target = name || icon || "";
  if (target && typeof target === "string" && (target.startsWith("/") || target.includes(".png"))) {
    return <img src={target} alt={name || "category"} style={{ width: size, height: size, objectFit: "contain" }} />;
  }
  const url = getCatIconUrl(target);
  return <img src={url} alt={target} style={{ width: size, height: size, objectFit: "contain" }} />;
}

const CreditCardsPage = () => {
  const { user } = useAuth();
  const userCurrency = user?.currency || "COP";
  const [paymentRequestId, setPaymentRequestId] = useState(() => window.crypto.randomUUID());
  const [paymentAccount, setPaymentAccount] = useState("unassigned");
  const [paymentCurrency, setPaymentCurrency] = useState("");
  const [paymentRates, setPaymentRates] = useState({});
  const displayMoney = (amount, currency = userCurrency) => amount == null ? "Sin valoración" : `${currency} ${formatMoney(amount, currency, { style: "decimal" })}`;
  const { isExpired } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedPaymentIndex, setSelectedPaymentIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [cardHistory, setCardHistory] = useState(null);
  const [categoryDict, setCategoryDict] = useState({});
  
  const [formData, setFormData] = useState({
    name: "",
    currency: userCurrency,
    credit_limit: "",
    current_debt: "",
    cut_off_day: "",
    payment_day: "",
    icon: "/Iconos/Tarjetas/3f97219eb4612ccb3677394e92fcb0d4062d2084.png"
  });
  
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNote, setPaymentNote] = useState("");

  const fetchCards = async () => {
    try {
      const [cardsResult, budgetsResult] = await Promise.allSettled([
        api.get("/credit-cards"),
        api.get("/category-budgets")
      ]);
      if (cardsResult.status === "rejected") {
        throw cardsResult.reason;
      }
      const resCards = cardsResult.value;
      setCards(resCards.data);
      if (budgetsResult.status === "fulfilled" && budgetsResult.value.data?.budgets) {
        const dict = {};
        budgetsResult.value.data.budgets.forEach((b) => (dict[b.name] = b.icon));
        setCategoryDict(dict);
      }
    } catch (error) {
      toast.error("Error al cargar tarjetas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  useEffect(() => {
    if (searchParams.get("action") !== "add") return;
    if (!isExpired) {
      setSelectedCard(null);
      setFormData({ name: "", currency: userCurrency, credit_limit: "", current_debt: "", cut_off_day: "", payment_day: "", icon: "/Iconos/Tarjetas/3f97219eb4612ccb3677394e92fcb0d4062d2084.png" });
      setDialogOpen(true);
    }
    setSearchParams({ tab: "cards" }, { replace: true });
  }, [searchParams, isExpired, setSearchParams, userCurrency]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error("El nombre es requerido");
      return;
    }

    const creditLimit = formData.credit_limit ? parseFloat(formData.credit_limit) : null;
    const currentDebt = formData.current_debt ? parseFloat(formData.current_debt) : 0;
    if (creditLimit !== null && (!Number.isFinite(creditLimit) || creditLimit <= 0)) {
      toast.error("Ingresa un cupo máximo válido");
      return;
    }
    if (!Number.isFinite(currentDebt) || currentDebt < 0) {
      toast.error("Ingresa una deuda válida");
      return;
    }
    if (creditLimit !== null && currentDebt > creditLimit) {
      toast.error("La deuda no puede ser mayor que el cupo máximo");
      return;
    }
    for (const [label, value] of [["día de corte", formData.cut_off_day], ["día de pago", formData.payment_day]]) {
      if (value && (Number(value) < 1 || Number(value) > 31)) {
        toast.error(`El ${label} debe estar entre 1 y 31`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        currency: formData.currency || userCurrency,
        credit_limit: creditLimit,
        current_debt: currentDebt,
        cut_off_day: formData.cut_off_day ? parseInt(formData.cut_off_day) : null,
        payment_day: formData.payment_day ? parseInt(formData.payment_day) : null,
        icon: formData.icon || "💳"
      };

      if (selectedCard) {
        await api.put(`/credit-cards/${selectedCard.id}`, payload);
        toast.success("Tarjeta actualizada");
      } else {
        await api.post("/credit-cards", payload);
        toast.success("Tarjeta agregada");
      }
      
      setDialogOpen(false);
      setFormData({ name: "", currency: userCurrency, credit_limit: "", current_debt: "", cut_off_day: "", payment_day: "", icon: "/Iconos/Tarjetas/3f97219eb4612ccb3677394e92fcb0d4062d2084.png" });
      setSelectedCard(null);
      fetchCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar tarjeta");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (cardId) => {
    try {
      await api.delete(`/credit-cards/${cardId}`);
      toast.success("Tarjeta eliminada");
      setDialogOpen(false);
      setSelectedCard(null);
      setFormData({ name: "", currency: userCurrency, credit_limit: "", current_debt: "", cut_off_day: "", payment_day: "", icon: "/Iconos/Tarjetas/3f97219eb4612ccb3677394e92fcb0d4062d2084.png" });
      fetchCards();
    } catch (error) {
      toast.error("Error al eliminar tarjeta");
    }
  };

  const handlePayment = async () => {
    const rawAmount = parseFloat(paymentAmount);
    if (!paymentAmount || rawAmount <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/credit-cards/${selectedCard.id}/payment`, {
        amount: rawAmount,
        currency: paymentCurrency || selectedCard.currency || userCurrency,
        exchange_rates: paymentRates,
        request_id: paymentRequestId,
        account_id: paymentAccount,
        note: paymentNote || null
      });
      setPaymentRequestId(window.crypto.randomUUID());
      toast.success("Pago registrado");
      setPaymentDialogOpen(false);
      setPaymentAmount("");
      setPaymentNote("");
      setSelectedCard(null);
      fetchCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar pago");
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewHistory = async (card) => {
    setSelectedCard(card);
    try {
      const res = await api.get(`/credit-cards/${card.id}/history`);
      setCardHistory(res.data);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error("Error al cargar historial");
    }
  };

  const openEditPaymentDialog = (payment, index) => {
    setSelectedPaymentIndex(index);
    setPaymentAmount(payment.amount?.toString() || "");
    setPaymentNote(payment.note || "");
    setEditPaymentDialogOpen(true);
  };

  const handleEditPayment = async () => {
    if (!selectedCard || selectedPaymentIndex === null || !paymentAmount) return;
    
    setSubmitting(true);
    try {
      await api.put(`/credit-cards/${selectedCard.id}/payments/${selectedPaymentIndex}`, {
        amount: parseFloat(paymentAmount),
        note: paymentNote || null
      });
      toast.success("Pago actualizado");
      setEditPaymentDialogOpen(false);
      setPaymentAmount("");
      setPaymentNote("");
      setSelectedPaymentIndex(null);
      const res = await api.get(`/credit-cards/${selectedCard.id}/history`);
      setCardHistory(res.data);
      fetchCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar pago");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePayment = async (index) => {
    if (!selectedCard) return;
    if (!window.confirm("¿Eliminar este pago? El monto se sumará nuevamente a la deuda de la tarjeta.")) return;
    try {
      await api.delete(`/credit-cards/${selectedCard.id}/payments/${index}`);
      toast.success("Pago eliminado");
      const res = await api.get(`/credit-cards/${selectedCard.id}/history`);
      setCardHistory(res.data);
      fetchCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar pago");
    }
  };

  const handleDeleteCharge = async (transactionId) => {
    if (!window.confirm("¿Eliminar esta compra? Se reducirá la deuda de la tarjeta.")) return;
    try {
      await api.delete(`/transactions/${transactionId}`);
      toast.success("Compra eliminada");
      const res = await api.get(`/credit-cards/${selectedCard.id}/history`);
      setCardHistory(res.data);
      fetchCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar compra");
    }
  };

  const openEditDialog = (card) => {
    setSelectedCard(card);
    setFormData({
      name: card.name,
      currency: card.currency || userCurrency,
      credit_limit: card.credit_limit ? String(card.credit_limit) : "",
      current_debt: card.current_debt ? String(card.current_debt) : "0",
      cut_off_day: card.cut_off_day?.toString() || "",
      payment_day: card.payment_day?.toString() || "",
      icon: card.icon || "/Iconos/Tarjetas/3f97219eb4612ccb3677394e92fcb0d4062d2084.png"
    });
    setDialogOpen(true);
  };

  const openPaymentDialog = (card) => {
    setSelectedCard(card);
    setPaymentCurrency(card.currency || userCurrency);
    setPaymentRates({});
    setPaymentDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
  };

  const totalDebt = cards.reduce((sum, c) => sum + (c.reporting_debt ?? (c.currency === userCurrency ? c.current_debt : 0) ?? 0), 0);
  const totalLimit = cards.reduce((sum, c) => sum + (c.reporting_limit ?? (c.currency === userCurrency ? c.credit_limit : 0) ?? 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="pb-32 md:pb-12 max-w-7xl mx-auto w-full">
      {/* Overview Cards - Figma Style */}
      <div className="aorra-cc-summary" style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        gap: isMobile ? "16px" : "24px",
        width: "100%",
        marginBottom: "32px",
        marginTop: "8px",
      }}>
        {/* Cupo total disponible */}
        <div style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          padding: isMobile ? "12px 20px" : "9px 30px",
          gap: isMobile ? "16px" : "30px",
          height: isMobile ? "100px" : "123px",
          background: "#FFFFFF",
          boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
          borderRadius: "20px",
          boxSizing: "border-box",
        }}>
          <div style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: isMobile ? "16px" : "24px",
            width: "100%"
          }}>
            {/* Cupo icon */}
            <div style={{
              width: isMobile ? "56px" : "80px",
              height: isMobile ? "56px" : "80px",
              borderRadius: "50%",
              background: "#F0FDFD",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}>
              <img src="/Iconos/Tarjetas/cupo_disponible.png" alt="Cupo" style={{ width: isMobile ? "34px" : "49.23px", height: isMobile ? "34px" : "49.23px", objectFit: "contain" }} />
            </div>
            {/* Text */}
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "100%" }}>
              <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: isMobile ? "15px" : "18px",
                lineHeight: isMobile ? "20px" : "25px",
                color: "#47484C",
              }}>Cupo total disponible en tarjetas</span>
              <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: isMobile ? "20px" : "24px",
                lineHeight: isMobile ? "26px" : "32px",
                color: "#62BF92",
              }}>{cards.some(c => c.valuation_unavailable) ? "Sin valoración" : displayMoney(Math.max(0, totalLimit - totalDebt))}</span>
            </div>
          </div>
        </div>

        {/* Esto debes en tus tarjetas */}
        <div style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          padding: isMobile ? "12px 20px" : "9px 30px",
          gap: isMobile ? "16px" : "30px",
          height: isMobile ? "100px" : "123px",
          background: "#FFFFFF",
          boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
          borderRadius: "20px",
          boxSizing: "border-box",
        }}>
          <div style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: isMobile ? "16px" : "24px",
            width: "100%"
          }}>
            {/* Deuda icon */}
            <div style={{
              width: isMobile ? "56px" : "80px",
              height: isMobile ? "56px" : "80px",
              borderRadius: "50%",
              background: "#F0FDFD",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}>
              <img src="/Iconos/Tarjetas/deuda_tarjetas.png" alt="Deuda" style={{ width: isMobile ? "34px" : "49.23px", height: isMobile ? "34px" : "49.23px", objectFit: "contain" }} />
            </div>
            {/* Text */}
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "100%" }}>
              <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: isMobile ? "15px" : "18px",
                lineHeight: isMobile ? "20px" : "25px",
                color: "#47484C",
              }}>Esto debes en tus tarjetas</span>
              <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: isMobile ? "20px" : "24px",
                lineHeight: isMobile ? "26px" : "32px",
                color: "#FF0040",
              }}>{cards.some(c => c.valuation_unavailable) ? "Sin valoración" : displayMoney(totalDebt)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Reusable Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        if (isExpired && open) return;
        setDialogOpen(open);
        if (!open) {
          setSelectedCard(null);
          setFormData({ name: "", currency: userCurrency, credit_limit: "", current_debt: "", cut_off_day: "", payment_day: "", icon: "/Iconos/Tarjetas/3f97219eb4612ccb3677394e92fcb0d4062d2084.png" });
        }
      }}>
          <DialogContent
            hideClose
            className="p-0 border-0"
            style={{
              width: "460px",
              maxWidth: "460px",
              overflow: "visible",
              background: "#FFFFFF",
              boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
              borderRadius: "20px",
              padding: "24px",
            }}
          >
            {/* Close X */}
            <button
              type="button"
              onClick={() => { setDialogOpen(false); setSelectedCard(null); setFormData({ name: "", currency: userCurrency, credit_limit: "", current_debt: "", cut_off_day: "", payment_day: "", icon: "/Iconos/Tarjetas/3f97219eb4612ccb3677394e92fcb0d4062d2084.png" }); }}
              style={{ position: "absolute", top: "14px", right: "14px", width: "24px", height: "24px", border: "none", background: "none", cursor: "pointer", padding: 0 }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <line x1="6" y1="6" x2="18" y2="18" stroke="#255E99" strokeWidth="2" strokeLinecap="round"/>
                <line x1="18" y1="6" x2="6" y2="18" stroke="#255E99" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>

            <form noValidate onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", width: "100%" }}>
              
              {/* Title */}
              <h2 style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: "20px",
                lineHeight: "28px",
                color: "#1B2559",
                textAlign: "center",
                margin: 0,
              }}>{selectedCard ? "Configurar tarjeta" : "Agregar nueva tarjeta"}</h2>

              {/* Dashed circle preview */}
              <div style={{
                width: "80px",
                height: "80px",
                borderRadius: "100px",
                border: "1px dashed #4A4A4A",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "6px",
                boxSizing: "border-box",
              }}>
                <img
                  src={formData.icon && formData.icon.startsWith("/") ? formData.icon : "/Iconos/Tarjetas/3f97219eb4612ccb3677394e92fcb0d4062d2084.png"}
                  alt="Selected icon"
                  style={{ width: "58px", height: "58px", objectFit: "contain" }}
                />
              </div>

            {/* Form fields */}
              <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
                
                <CurrencySelect value={formData.currency} portalCurrency={userCurrency} disabled={Boolean(selectedCard)} onChange={currency => setFormData({ ...formData, currency })} label="Moneda de la tarjeta" />
                {/* Nombre */}
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "14px", lineHeight: "20px", color: "#5C5C5C" }}>Nombre de la tarjeta</label>
                  <input
                    type="text"
                    placeholder="Mi tarjeta BBVA"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    style={{
                      boxSizing: "border-box", width: "100%", height: "42px", padding: "10px 12px",
                      border: "1px solid #DDDDDD", borderRadius: "20px", outline: "none",
                      fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: "15px", color: "#4A4A4A",
                    }}
                  />
                </div>

                {/* Cupo + Deuda row */}
                <div style={{ display: "flex", flexDirection: "row", gap: "12px" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1 }}>
                    <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "14px", lineHeight: "20px", color: "#5C5C5C" }}>Cupo máximo</label>
                    <div style={{ position: "relative" }}>
                      <CurrencyInput
                        currency={formData.currency || userCurrency}
                        placeholder="0"
                        value={formData.credit_limit}
                        onChange={(e) => setFormData({...formData, credit_limit: e.target.value})}
                        style={{
                          boxSizing: "border-box", width: "100%", height: "42px", padding: "10px 12px 10px 24px",
                          border: "1px solid #DDDDDD", borderRadius: "20px", outline: "none",
                          fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: "15px", color: "#4A4A4A",
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1 }}>
                    <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "14px", lineHeight: "20px", color: "#5C5C5C" }}>Deuda a la fecha</label>
                    <div style={{ position: "relative" }}>
                      <CurrencyInput
                        currency={formData.currency || userCurrency}
                        placeholder="0"
                        value={formData.current_debt}
                        onChange={(e) => setFormData({...formData, current_debt: e.target.value})}
                        style={{
                          boxSizing: "border-box", width: "100%", height: "42px", padding: "10px 12px 10px 24px",
                          border: "1px solid #DDDDDD", borderRadius: "20px", outline: "none",
                          fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: "15px", color: "#4A4A4A",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Corte + Pago row */}
                <div style={{ display: "flex", flexDirection: "row", gap: "12px" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1 }}>
                    <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "14px", lineHeight: "20px", color: "#5C5C5C" }}>Día de corte</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max="31"
                      step="1"
                      placeholder="6"
                      value={formData.cut_off_day}
                      onChange={(e) => setFormData({...formData, cut_off_day: e.target.value.replace(/\D/g, "").slice(0, 2)})}
                      onKeyDown={(e) => {
                        if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
                      }}
                      style={{
                        boxSizing: "border-box", width: "100%", height: "42px", padding: "10px 12px",
                        border: "1px solid #DDDDDD", borderRadius: "20px", outline: "none",
                        fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: "15px", color: "#4A4A4A",
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1 }}>
                    <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "14px", lineHeight: "20px", color: "#5C5C5C" }}>Día de pago</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max="31"
                      step="1"
                      placeholder="15"
                      value={formData.payment_day}
                      onChange={(e) => setFormData({...formData, payment_day: e.target.value.replace(/\D/g, "").slice(0, 2)})}
                      onKeyDown={(e) => {
                        if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
                      }}
                      style={{
                        boxSizing: "border-box", width: "100%", height: "42px", padding: "10px 12px",
                        border: "1px solid #DDDDDD", borderRadius: "20px", outline: "none",
                        fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: "15px", color: "#4A4A4A",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Icon selector */}
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "12px" }}>
                <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "14px", lineHeight: "20px", color: "#5C5C5C" }}>Selecciona un icono para tu tarjeta</label>
                <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "8px", justifyContent: "center" }}>
                  {[
                    { src: "/Iconos/Tarjetas/3f97219eb4612ccb3677394e92fcb0d4062d2084.png", label: "Tarjeta Verde" },
                    { src: "/Iconos/Tarjetas/344060604f6181a3e34af3afaeb8e1646489e605.png", label: "Grid Plus" },
                    { src: "/Iconos/Tarjetas/39e9ba9f584b792b5ce22f979561891c08ecb6d0.png", label: "Regalo" },
                    { src: "/Iconos/Tarjetas/39ea140915af7b7deecea6146e74ca0b934e7068.png", label: "Cohete" },
                    { src: "/Iconos/Tarjetas/839d897b5040922d846808c1baa54417ac21f982.png", label: "Fuego" },
                    { src: "/Iconos/Tarjetas/a59366e1f86a701e417d99c5fde16a10a77bf64d.png", label: "Cámara" },
                  ].map((item) => {
                    const isSelected = formData.icon === item.src;
                    return (
                      <button
                        key={item.src}
                        type="button"
                        onClick={() => setFormData({...formData, icon: item.src})}
                        style={{
                          boxSizing: "border-box",
                          width: "60px",
                          height: "61px",
                          background: "rgba(225, 233, 252, 0.73)",
                          border: isSelected ? "2px solid #396BE9" : "1px solid rgba(225, 233, 252, 0.73)",
                          borderRadius: "16px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          position: "relative",
                          padding: "4px",
                          transition: "border 0.15s ease",
                        }}
                      >
                        <img src={item.src} alt={item.label} style={{ width: "42px", height: "42px", objectFit: "contain" }} />
                        {isSelected && (
                          <div style={{
                            position: "absolute",
                            top: "-4px",
                            right: "-4px",
                            width: "20px",
                            height: "20px",
                            background: "#C7FF65",
                            borderRadius: "100px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                              <polyline points="20 6 9 17 4 12" stroke="#141B34" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: "100%",
                  height: "46px",
                  background: "#396BE9",
                  borderRadius: "16px",
                  border: "none",
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 500,
                  fontSize: "15px",
                  color: "#FFFFFF",
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? "Guardando..." : "Guardar tarjeta"}
              </button>

              {selectedCard && !isExpired && (
                <button
                  type="button"
                  onClick={() => { if(window.confirm('¿Eliminar esta tarjeta?')) handleDelete(selectedCard.id) }}
                  style={{
                    background: "none", border: "none",
                    fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: "13px",
                    color: "#FF0040", cursor: "pointer", padding: "2px 6px",
                    marginTop: "-4px"
                  }}
                >
                  Eliminar tarjeta
                </button>
              )}
            </form>
          </DialogContent>
        </Dialog>

      {cards.length === 0 ? (
        <div style={{
          width: "100%",
          background: "#FFFFFF",
          boxShadow: "0px 10px 30px rgba(176, 179, 189, 0.15)",
          borderRadius: "20px",
          padding: "60px 24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}>
          {/* Custom vector credit card empty state illustration */}
          <svg width="400" height="200" viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Background Clouds/Pills */}
            <rect x="100" y="80" width="220" height="24" rx="12" fill="#F3F7FF" />
            <rect x="130" y="55" width="180" height="24" rx="12" fill="#F3F7FF" />
            <rect x="70" y="105" width="250" height="24" rx="12" fill="#F3F7FF" />
            <circle cx="340" cy="130" r="12" fill="#E7F6FF" />

            {/* Background Credit Card (Tilted) */}
            <g transform="translate(145, 40) rotate(-6)">
              <rect x="0" y="0" width="130" height="85" rx="8" fill="#FFFFFF" stroke="#1F64E7" stroke-width="2.5" />
              <rect x="5" y="5" width="120" height="75" rx="6" fill="#E8F0FE" opacity="0.7" />
            </g>

            {/* Foreground Credit Card (Horizontal) */}
            <g transform="translate(165, 75)">
              {/* Base Card */}
              <rect x="0" y="0" width="130" height="85" rx="8" fill="#FFFFFF" stroke="#1F64E7" stroke-width="2.5" />
              {/* Magnetic stripe / top line */}
              <path d="M12,18 L30,18" stroke="#1F64E7" stroke-width="2.5" stroke-linecap="round" />
              <path d="M38,18 L118,18" stroke="#1F64E7" stroke-width="2.5" stroke-linecap="round" />
              {/* Middle line */}
              <path d="M12,35 L118,35" stroke="#1F64E7" stroke-width="2.5" stroke-linecap="round" />
              {/* Bottom line */}
              <path d="M22,60 L78,60" stroke="#1F64E7" stroke-width="2.5" stroke-linecap="round" />
            </g>
          </svg>

          {/* Texts */}
          <h3 style={{
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 700,
            fontSize: "18px",
            color: "#1B2559",
            margin: "20px 0 6px 0",
            textAlign: "center"
          }}>Sin resultados</h3>
          <p style={{
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 500,
            fontSize: "14px",
            color: "#687377",
            margin: "0 0 24px 0",
            textAlign: "center"
          }}>No tienes tarjetas agregadas</p>

          {/* Button */}
          <button
            onClick={() => {
              if (isExpired) return;
              setFormData({ name: "", currency: userCurrency, credit_limit: "", current_debt: "", cut_off_day: "", payment_day: "", icon: "/Iconos/Tarjetas/3f97219eb4612ccb3677394e92fcb0d4062d2084.png" });
              setSelectedCard(null);
              setDialogOpen(true);
            }}
            disabled={isExpired}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              width: "180px",
              height: "46px",
              background: "#396BE9",
              borderRadius: "16px",
              border: "none",
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 600,
              fontSize: "15px",
              color: "#FFFFFF",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(57, 107, 233, 0.25)"
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#FFFFFF" strokeWidth="2"/>
              <line x1="12" y1="8" x2="12" y2="16" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
              <line x1="8" y1="12" x2="16" y2="12" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Agregar tarjeta
          </button>
        </div>
      ) : (
        <div className="aorra-cc-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "24px" }}>
          {cards.map((card, idx) => {
            const usagePercent = card.credit_limit ? (card.current_debt / card.credit_limit) * 100 : 0;
            const theme = THEMES[idx % THEMES.length];
            
            return (
              <div key={card.id} style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "20px",
                gap: "16px",
                width: "100%",
                height: "310px",
                background: "#FFFFFF",
                boxShadow: "2px 8px 20px rgba(32, 85, 95, 0.2)",
                borderRadius: "16px",
                boxSizing: "border-box",
                justifyContent: "space-between",
              }}>
                {/* Header row: icon + name + edit */}
                <div style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}>
                  <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
                    {/* Mini card icon */}
                    <div style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      background: "#DBFBF8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <img
                        src={card.icon && card.icon.startsWith("/") ? card.icon : "/Iconos/Tarjetas/3f97219eb4612ccb3677394e92fcb0d4062d2084.png"}
                        alt="Card icon"
                        style={{ width: "34px", height: "34px", objectFit: "contain" }}
                      />
                    </div>
                    {/* Name and date */}
                    <div style={{ minWidth: 0 }}>
                      <p style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 700,
                        fontSize: "18px",
                        lineHeight: "25px",
                        color: "#4A4A4A",
                        margin: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>{card.name} · {card.currency || userCurrency}</p>
                      <p style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "19px",
                        color: "#687377",
                        margin: 0,
                      }}>Paga el día <span style={{ color: "#396BE9", fontWeight: 600 }}>{card.payment_day || '--'} de cada mes</span></p>
                    </div>
                  </div>
                  {/* Edit icon */}
                  <button
                    onClick={() => openEditDialog(card)}
                    style={{
                      width: "20px",
                      height: "20px",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      padding: 0,
                      flexShrink: 0,
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="#396BE9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>

                {/* Debt info + progress */}
                <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "12px" }}>
                  {/* Deuda actual */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "19px",
                      color: "#687377",
                    }}>Deuda actual</span>
                    <span style={{
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 800,
                      fontSize: "20px",
                      lineHeight: "27px",
                      color: "#4A4A4A",
                    }}>{displayMoney(card.current_debt || 0, card.currency || userCurrency)}</span>
                    {card.currency !== userCurrency && <small>≈ {displayMoney(card.reporting_debt)} · {card.valuation_quote?.date}</small>}
                  </div>

                  {/* Progress bar */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <div style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}>
                      <span style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "19px",
                        color: "#687377",
                      }}>Límite ({displayMoney(card.credit_limit || 0, card.currency || userCurrency)})</span>
                      <span style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 700,
                        fontSize: "14px",
                        lineHeight: "19px",
                        color: "#4A4A4A",
                      }}>{usagePercent.toFixed(0)}%</span>
                    </div>
                    <div style={{
                      width: "100%",
                      height: "6px",
                      background: "#E1E9FC",
                      borderRadius: "3px",
                      position: "relative",
                    }}>
                      <div style={{
                        width: `${Math.min(100, usagePercent)}%`,
                        height: "6px",
                        background: usagePercent > 80 ? "#FF0040" : "#82FFC3",
                        borderRadius: "3px",
                        position: "absolute",
                        top: 0,
                        left: 0,
                      }} />
                    </div>
                  </div>
                </div>

                {/* Actions Footer */}
                <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px" }}>
                  <button
                    onClick={() => handleViewHistory(card)}
                    style={{
                      width: "100%",
                      background: "none",
                      border: "none",
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#396BE9",
                      cursor: "pointer",
                      padding: "4px 0",
                      textAlign: "center",
                      transition: "opacity 0.15s ease",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = "0.75"}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                  >
                    Ver historial
                  </button>

                  <button
                    onClick={() => openPaymentDialog(card)}
                    disabled={card.current_debt <= 0 || isExpired}
                    style={{
                      width: "100%",
                      height: "42px",
                      background: "#396BE9",
                      borderRadius: "16px",
                      border: "none",
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 500,
                      fontSize: "16px",
                      color: "#FFFFFF",
                      cursor: card.current_debt <= 0 || isExpired ? "not-allowed" : "pointer",
                      opacity: card.current_debt <= 0 ? 0.5 : 1,
                      transition: "opacity 0.15s ease",
                    }}
                    onMouseEnter={(e) => { if (card.current_debt > 0 && !isExpired) e.currentTarget.style.opacity = "0.85"; }}
                    onMouseLeave={(e) => { if (card.current_debt > 0) e.currentTarget.style.opacity = "1"; }}
                  >
                    Pagar ahora
                  </button>
                </div>
              </div>
            );
          })}

          {/* Add New Card - adapts based on card count */}
          <div style={{ gridColumn: cards.length >= 1 && cards.length <= 2 ? "auto" : "1 / -1", marginTop: cards.length >= 1 && cards.length <= 2 ? "0" : "8px" }}>
            <div 
              onClick={() => {
                if (!isExpired) {
                  setFormData({ name: "", currency: userCurrency, credit_limit: "", current_debt: "", cut_off_day: "", payment_day: "", icon: "/Iconos/Tarjetas/3f97219eb4612ccb3677394e92fcb0d4062d2084.png" });
                  setSelectedCard(null);
                  setDialogOpen(true);
                }
              }}
              style={{
                width: "100%",
                height: cards.length >= 1 && cards.length <= 2 ? "310px" : "120px",
                background: "transparent",
                border: "2px dashed rgba(57, 107, 233, 0.4)",
                borderRadius: "16px",
                padding: "24px",
                cursor: isExpired ? "not-allowed" : "pointer",
                opacity: isExpired ? 0.5 : 1,
                display: "flex",
                flexDirection: cards.length >= 1 && cards.length <= 2 ? "column" : "row",
                alignItems: "center",
                justifyContent: "center",
                gap: "20px",
                transition: "all 0.2s ease",
                boxSizing: "border-box",
              }}
              onMouseEnter={(e) => {
                if (!isExpired) {
                  e.currentTarget.style.background = "#FFFFFF";
                  e.currentTarget.style.borderColor = "#396BE9";
                  e.currentTarget.style.boxShadow = "2px 8px 20px rgba(32, 85, 95, 0.15)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isExpired) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.borderColor = "rgba(57, 107, 233, 0.4)";
                  e.currentTarget.style.boxShadow = "none";
                }
              }}
            >
              <div style={{
                width: cards.length >= 1 && cards.length <= 2 ? "80px" : "48px",
                height: cards.length >= 1 && cards.length <= 2 ? "80px" : "48px",
                background: "#E1E9FC",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#396BE9" strokeWidth="3" strokeLinecap="round" style={{ width: cards.length >= 1 && cards.length <= 2 ? "32px" : "24px", height: cards.length >= 1 && cards.length <= 2 ? "32px" : "24px" }}>
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </div>
              <div style={{ textAlign: cards.length >= 1 && cards.length <= 2 ? "center" : "left" }}>
                <p style={{
                  margin: 0,
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 800,
                  fontSize: "18px",
                  color: "#4A4A4A",
                }}>Agregar nueva tarjeta</p>
                <p style={{
                  margin: "4px 0 0 0",
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 500,
                  fontSize: "14px",
                  color: "#687377",
                }}>Haz clic para comenzar</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent
          hideClose
          className="p-0 border-0"
          style={{
            width: "460px",
            maxWidth: "460px",
            overflow: "visible",
            background: "#FFFFFF",
            boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
            borderRadius: "20px",
            padding: "24px",
          }}
        >
          {/* Close X */}
          <button
            type="button"
            onClick={() => setPaymentDialogOpen(false)}
            style={{ position: "absolute", top: "14px", right: "14px", width: "24px", height: "24px", border: "none", background: "none", cursor: "pointer", padding: 0 }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <line x1="6" y1="6" x2="18" y2="18" stroke="#255E99" strokeWidth="2" strokeLinecap="round"/>
              <line x1="18" y1="6" x2="6" y2="18" stroke="#255E99" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", width: "100%" }}>
            {/* Title */}
            <h2 style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "28px",
              color: "#1B2559",
              textAlign: "center",
              margin: 0,
            }}>Abonar a {selectedCard?.name}</h2>

            {/* Card icon preview */}
            <div style={{
              width: "80px",
              height: "80px",
              borderRadius: "100px",
              background: "#DBFBF8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <img
                src={selectedCard?.icon || "/Iconos/Tarjetas/3f97219eb4612ccb3677394e92fcb0d4062d2084.png"}
                alt="Tarjeta"
                style={{ width: "58px", height: "58px", objectFit: "contain" }}
              />
            </div>

            {/* Deuda actual box */}
            <div style={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              padding: "0px 20px",
              justifyContent: "space-between",
              width: "100%",
              height: "53px",
              border: "2px solid #CDF7F7",
              borderRadius: "16px",
            }}>
              <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: "16px",
                color: "#5C5C5C",
              }}>Deuda actual</span>
              <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: "20px",
                color: "#4A4A4A",
              }}>{displayMoney(selectedCard?.current_debt || 0, selectedCard?.currency || userCurrency)}</span>
            </div>

            <CurrencySelect value={paymentCurrency} portalCurrency={userCurrency} onChange={value => { setPaymentCurrency(value); setPaymentRates({}); }} label="Moneda del abono" />
            <AccountSelect value={paymentAccount} onChange={(id, account) => { setPaymentAccount(id); if (account) setPaymentCurrency(account.currency); }} />
            <FXPreview amount={paymentAmount} currency={paymentCurrency || selectedCard?.currency || userCurrency} targets={[userCurrency, selectedCard?.currency]} rates={paymentRates} onRatesChange={setPaymentRates} />
            {/* Form fields */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
              {/* Monto a abonar */}
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "14px", lineHeight: "20px", color: "#5C5C5C" }}>Monto a abonar</label>
                <div style={{ position: "relative" }}>
                  <CurrencyInput currency={editPaymentDialogOpen ? selectedCard?.currency || userCurrency : paymentCurrency || selectedCard?.currency || userCurrency} placeholder="0" value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    style={{
                      boxSizing: "border-box", width: "100%", height: "42px", padding: "10px 12px 10px 24px",
                      border: "1px solid #DDDDDD", borderRadius: "20px", outline: "none",
                      fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: "15px", color: "#4A4A4A",
                    }}
                  />
                </div>
              </div>

              {/* Nota */}
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "14px", lineHeight: "20px", color: "#5C5C5C" }}>Nota (opcional)</label>
                <input
                  type="text"
                  placeholder="Ej: Pago total del mes"
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  style={{
                    boxSizing: "border-box", width: "100%", height: "42px", padding: "10px 12px",
                    border: "1px solid #DDDDDD", borderRadius: "20px", outline: "none",
                    fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: "15px", color: "#4A4A4A",
                  }}
                />
              </div>
            </div>

            {/* Submit button */}
            <button
              onClick={handlePayment}
              disabled={submitting}
              style={{
                width: "100%",
                height: "46px",
                background: "#396BE9",
                borderRadius: "16px",
                border: "none",
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 500,
                fontSize: "15px",
                color: "#FFFFFF",
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.7 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                transition: "opacity 0.15s",
              }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar Pago"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog with premium custom styling */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent
          hideClose
          className="p-0 border-0"
          style={{
            width: "520px",
            maxWidth: "520px",
            background: "#FFFFFF",
            boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
            borderRadius: "20px",
            padding: "24px",
            overflow: "visible",
          }}
        >
          {/* Close X */}
          <button
            type="button"
            onClick={() => setHistoryDialogOpen(false)}
            style={{ position: "absolute", top: "14px", right: "14px", width: "24px", height: "24px", border: "none", background: "none", cursor: "pointer", padding: 0 }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <line x1="6" y1="6" x2="18" y2="18" stroke="#255E99" strokeWidth="2" strokeLinecap="round"/>
              <line x1="18" y1="6" x2="6" y2="18" stroke="#255E99" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>

          <div style={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%" }}>
            {/* Title */}
            <h2 style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "28px",
              color: "#1B2559",
              margin: 0,
            }}>Historial de {selectedCard?.name}</h2>

            <div style={{ display: "flex", flexDirection: "column", gap: "24px", maxHeight: "60vh", overflowY: "auto", paddingRight: "6px" }} className="custom-scrollbar">
              {(!cardHistory?.payments?.length && !cardHistory?.charges?.length) ? (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "40px 24px",
                  background: "#FFFFFF",
                  width: "100%",
                  boxSizing: "border-box"
                }}>
                  <svg width="250" height="200" viewBox="0 0 250 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="250" height="200" fill="white"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M207 65C210.866 65 214 68.134 214 72C214 75.866 210.866 79 207 79H167C170.866 79 174 82.134 174 86C174 89.866 170.866 93 167 93H189C192.866 93 196 96.134 196 100C196 103.866 192.866 107 189 107H178.826C173.952 107 170 110.134 170 114C170 116.577 172 118.911 176 121C179.866 121 183 124.134 183 128C183 131.866 179.866 135 176 135H93C89.134 135 86 131.866 86 128C86 124.134 89.134 121 93 121H54C50.134 121 47 117.866 47 114C47 110.134 50.134 107 54 107H94C97.866 107 101 103.866 101 100C101 96.134 97.866 93 94 93H69C65.134 93 62 89.866 62 86C62 82.134 65.134 79 69 79H109C105.134 79 102 75.866 102 72C102 68.134 105.134 65 109 65H207ZM207 93C210.866 93 214 96.134 214 100C214 103.866 210.866 107 207 107C203.134 107 200 103.866 200 100C200 96.134 203.134 93 207 93Z" fill="#F3F7FF"/>
                    <path d="M120.5 133C139.002 133 154 118.002 154 99.5C154 80.9985 139.002 66 120.5 66C101.998 66 87 80.9985 87 99.5C87 118.002 101.998 133 120.5 133Z" fill="#F3F7FF" stroke="#1F64E7" stroke-width="2.5"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M115.132 125.494C116.891 125.819 118.68 125.987 120.5 126C135.136 126 147 114.136 147 99.5C147 84.8645 135.136 73 120.5 73C116.74 73 113.164 73.7829 109.924 75.1946C104.294 77.6479 99.6816 81.9999 96.896 87.4419C95.0445 91.0589 94 95.1575 94 99.5C94 103.44 94.8599 107.179 96.4021 110.54C97.5032 112.94 98.9521 115.146 100.684 117.096" fill="white"/>
                    <path d="M115.132 125.494C116.891 125.819 118.68 125.987 120.5 126C135.136 126 147 114.136 147 99.5C147 84.8645 135.136 73 120.5 73C116.74 73 113.164 73.7829 109.924 75.1946C104.294 77.6479 99.6816 81.9999 96.896 87.4419C95.0445 91.0589 94 95.1575 94 99.5C94 103.44 94.8599 107.179 96.4021 110.54C97.5032 112.94 98.9521 115.146 100.684 117.096" stroke="#1F64E7" stroke-width="2.5" stroke-linecap="round"/>
                    <path d="M103.798 120.075C105.946 121.821 108.373 123.237 111.001 124.247" stroke="#1F64E7" stroke-width="2.5" stroke-linecap="round"/>
                    <path d="M148 126L154 132" stroke="#1F64E7" stroke-width="2.5"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M153.03 131.03C151.137 132.923 151.137 135.992 153.03 137.884L164.115 148.97C166.008 150.862 169.077 150.862 170.969 148.97C172.862 147.077 172.862 144.008 170.969 142.116L159.884 131.03C157.991 129.138 154.923 129.138 153.03 131.03Z" fill="#E8F0FE" stroke="#1F64E7" stroke-width="2.5"/>
                    <path d="M158 133L169 144" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M114.001 88C114.001 99.598 123.403 109 135.001 109C137.279 109 139.472 108.637 141.527 107.966C138.174 116.287 130.023 122.161 120.501 122.161C107.985 122.161 97.8398 112.015 97.8398 99.5C97.8398 88.1596 106.17 78.7648 117.046 77.1011C115.113 80.2793 114.001 84.0097 114.001 88Z" fill="#E8F0FE"/>
                    <path d="M121 81C119.727 81 118.482 81.1253 117.279 81.3642M113.645 82.4761C106.804 85.3508 102 92.1144 102 100" stroke="#75A4FE" stroke-width="2.5" stroke-linecap="round"/>
                    <path d="M174.176 99.7773H166M180.5 92H163.324H180.5ZM187.5 92H185.279H187.5Z" stroke="#75A4FE" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M84.1763 121.777H76.0005M79.5005 113H62.3247H79.5005ZM56.5005 113H52.2793H56.5005Z" stroke="#75A4FE" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <p style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#687377",
                    margin: "12px 0 0 0",
                    textAlign: "center"
                  }}>No se han registrado movimientos</p>
                </div>
              ) : (
                <>
                  {/* Abonos aplicados */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <h3 style={{
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 700,
                      fontSize: "13px",
                      color: "#8E8E8E",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      margin: "0 0 4px 0",
                    }}>Abonos Aplicados</h3>
                    {cardHistory?.payments?.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {cardHistory.payments.map((payment, idx) => (
                          <div key={idx} style={{
                            boxSizing: "border-box",
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 0px",
                            gap: "12px",
                            width: "100%",
                            height: "60px",
                            borderBottom: "1px solid #E0EDF8",
                          }}>
                            {/* Left content (icon + description) */}
                            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexGrow: 1, minWidth: 0 }}>
                              <div style={{
                                width: "38px",
                                height: "38px",
                                borderRadius: "50%",
                                background: "#F6F8FD",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0
                              }}>
                                <img src="/Iconos/Categorias/abono_tarjeta.png" alt="Abono" style={{ width: "20px", height: "20px", objectFit: "contain" }} />
                              </div>
                              <span style={{
                                fontFamily: "'Manrope', sans-serif",
                                fontWeight: 500,
                                fontSize: "15px",
                                lineHeight: "20px",
                                color: "#000000",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                              }}>
                                {payment.note || "Abono / Pago"}
                              </span>
                            </div>

                            {/* Right content (date + amount + actions) */}
                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "12px", flexShrink: 0 }}>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "2px" }}>
                                <span style={{
                                  fontFamily: "'Quicksand', sans-serif",
                                  fontWeight: 500,
                                  fontSize: "11px",
                                  lineHeight: "14px",
                                  color: "#8E8E8E"
                                }}>
                                  {formatDate(payment.date)}
                                </span>
                                <span style={{
                                  fontFamily: "'Manrope', sans-serif",
                                  fontWeight: 700,
                                  fontSize: "14px",
                                  lineHeight: "18px",
                                  color: "#396BE9"
                                }}>
                                  -{displayMoney(payment.amount, payment.currency || selectedCard?.currency || userCurrency)}
                                </span>
                              </div>
                              {!isExpired && (
                                <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                                  <button
                                    onClick={() => openEditPaymentDialog(payment, idx)}
                                    style={{ background: "none", border: "none", cursor: "pointer", padding: "4px" }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="#396BE9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleDeletePayment(idx)}
                                    style={{ background: "none", border: "none", cursor: "pointer", padding: "4px" }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <polyline points="3 6 5 6 21 6" stroke="#FF0040" strokeWidth="2" strokeLinecap="round"/>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="#FF0040" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "14px", color: "#687377", margin: 0, padding: "8px 0" }}>Sin abonos registrados.</p>
                    )}
                  </div>

                  {/* Consumos Registrados */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <h3 style={{
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 700,
                      fontSize: "13px",
                      color: "#8E8E8E",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      margin: "12px 0 4px 0",
                    }}>Consumos Registrados</h3>
                    {cardHistory?.charges?.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {cardHistory.charges.slice(0, 15).map((charge) => (
                          <div key={charge.id} style={{
                            boxSizing: "border-box",
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 0px",
                            gap: "12px",
                            width: "100%",
                            height: "60px",
                            borderBottom: "1px solid #E0EDF8",
                          }}>
                            {/* Left content (icon + description) */}
                            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexGrow: 1, minWidth: 0 }}>
                              <div style={{
                                width: "38px",
                                height: "38px",
                                borderRadius: "50%",
                                background: "#F6F8FD",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0
                              }}>
                                {getCatIcon(charge.icon, charge.category, 20)}
                              </div>
                              <span style={{
                                fontFamily: "'Manrope', sans-serif",
                                fontWeight: 500,
                                fontSize: "15px",
                                lineHeight: "20px",
                                color: "#000000",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                              }}>
                                {charge.description || charge.category}
                              </span>
                              <FXDetails transaction={charge} />
                            </div>

                            {/* Right content (date + amount + actions) */}
                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "12px", flexShrink: 0 }}>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "2px" }}>
                                <span style={{
                                  fontFamily: "'Quicksand', sans-serif",
                                  fontWeight: 500,
                                  fontSize: "11px",
                                  lineHeight: "14px",
                                  color: "#8E8E8E"
                                }}>
                                  {formatDate(charge.created_at)}
                                </span>
                                <span style={{
                                  fontFamily: "'Manrope', sans-serif",
                                  fontWeight: 700,
                                  fontSize: "14px",
                                  lineHeight: "18px",
                                  color: "#FF0040"
                                }}>
                                  -{displayMoney(charge.credit_card_amount ?? Math.abs(charge.amount), charge.credit_card_currency || selectedCard?.currency || userCurrency)}
                                </span>
                              </div>
                              {!isExpired && (
                                <button
                                  onClick={() => handleDeleteCharge(charge.id)}
                                  style={{ background: "none", border: "none", cursor: "pointer", padding: "4px" }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <polyline points="3 6 5 6 21 6" stroke="#FF0040" strokeWidth="2" strokeLinecap="round"/>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="#FF0040" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "14px", color: "#687377", margin: 0, padding: "8px 0" }}>No hay consumos en esta tarjeta.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
       <Dialog open={editPaymentDialogOpen} onOpenChange={(open) => {
         setEditPaymentDialogOpen(open);
         if (!open) {
           setPaymentAmount("");
           setPaymentNote("");
           setSelectedPaymentIndex(null);
         }
       }}>
         <DialogContent
           hideClose
           className="p-0 border-0"
           style={{
             width: "460px",
             maxWidth: "460px",
             background: "#FFFFFF",
             boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
             borderRadius: "20px",
             padding: "24px",
             overflow: "visible",
           }}
         >
           {/* Close X */}
           <button
             type="button"
             onClick={() => setEditPaymentDialogOpen(false)}
             style={{ position: "absolute", top: "14px", right: "14px", width: "24px", height: "24px", border: "none", background: "none", cursor: "pointer", padding: 0 }}
           >
             <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
               <line x1="6" y1="6" x2="18" y2="18" stroke="#255E99" strokeWidth="2" strokeLinecap="round"/>
               <line x1="18" y1="6" x2="6" y2="18" stroke="#255E99" strokeWidth="2" strokeLinecap="round"/>
             </svg>
           </button>

           <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
             {/* Title */}
             <h2 style={{
               fontFamily: "'Manrope', sans-serif",
               fontWeight: 700,
               fontSize: "18px",
               lineHeight: "25px",
               color: "#1B2559",
               margin: 0,
             }}>Modificar Abono</h2>

             <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
               {/* Monto */}
               <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                 <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "14px", color: "#5C5C5C" }}>Monto rectificado</label>
                 <div style={{ position: "relative" }}>
                   <CurrencyInput currency={editPaymentDialogOpen ? selectedCard?.currency || userCurrency : paymentCurrency || selectedCard?.currency || userCurrency} placeholder="0" value={paymentAmount}
                     onChange={(e) => setPaymentAmount(e.target.value)}
                     style={{
                       boxSizing: "border-box", width: "100%", height: "42px", padding: "10px 12px 10px 24px",
                       border: "1px solid #DDDDDD", borderRadius: "20px", outline: "none",
                       fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: "15px", color: "#4A4A4A",
                     }}
                   />
                 </div>
               </div>

               {/* Nota */}
               <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                 <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "14px", color: "#5C5C5C" }}>Nota descriptiva (opcional)</label>
                 <input
                   type="text"
                   placeholder="Ej: Pago mínimo"
                   value={paymentNote}
                   onChange={(e) => setPaymentNote(e.target.value)}
                   style={{
                     boxSizing: "border-box", width: "100%", height: "42px", padding: "10px 12px",
                     border: "1px solid #DDDDDD", borderRadius: "20px", outline: "none",
                     fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: "15px", color: "#4A4A4A",
                   }}
                 />
               </div>
             </div>

             <button
               onClick={handleEditPayment}
               disabled={submitting || !paymentAmount}
               style={{
                 width: "100%",
                 height: "46px",
                 background: "#396BE9",
                 borderRadius: "16px",
                 border: "none",
                 fontFamily: "'Manrope', sans-serif",
                 fontWeight: 500,
                 fontSize: "15px",
                 color: "#FFFFFF",
                 cursor: submitting || !paymentAmount ? "not-allowed" : "pointer",
                 opacity: submitting || !paymentAmount ? 0.7 : 1,
               }}
             >
               {submitting ? "Guardando..." : "Guardar Modificación"}
             </button>
           </div>
         </DialogContent>
       </Dialog>
      <style>{`
        @media (max-width: 900px) {
          .aorra-cc-summary {
            gap: 12px !important;
          }
          .aorra-cc-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CreditCardsPage;
